import { createFileRoute } from '@tanstack/react-router'
import { useQuery } from '@tanstack/react-query'
import { supabase } from '@/integrations/supabase/client'
import { useCompany } from '@/lib/CompanyProvider'
import { format, parseISO, startOfWeek, endOfWeek, addDays } from 'date-fns'
import { ptBR } from 'date-fns/locale'
import { Calendar as CalendarIcon, Clock, User, Car } from 'lucide-react'
import { useState } from 'react'

export const Route = createFileRoute('/app/agenda')({
  component: AgendaPage,
})

function AgendaPage() {
  const { activeCompanyId } = useCompany()
  const [currentDate, setCurrentDate] = useState(new Date())

  const weekStart = startOfWeek(currentDate, { weekStartsOn: 1 })
  const weekEnd = endOfWeek(currentDate, { weekStartsOn: 1 })

  const { data: appointments, isLoading } = useQuery({
    queryKey: ['agenda', activeCompanyId, weekStart.toISOString()],
    enabled: !!activeCompanyId,
    queryFn: async () => {
      const { data, error } = await supabase
        .from('orders')
        .select('id, scheduled_at, customer_name, vehicle_model, plate, status, assigned_to')
        .eq('company_id', activeCompanyId)
        .gte('scheduled_at', weekStart.toISOString())
        .lt('scheduled_at', weekEnd.toISOString())
        .order('scheduled_at', { ascending: true })
      
      if (error) throw error
      return data ?? []
    }
  })

  const { data: washers } = useQuery({
    queryKey: ['collaborators', activeCompanyId],
    enabled: !!activeCompanyId,
    queryFn: async () => {
      const { data } = await supabase
        .from('collaborators')
        .select('id, name')
        .eq('company_id', activeCompanyId)
      return data ?? []
    }
  })

  const getWasherName = (id: string | null) => {
    if (!id) return 'Sem preferência'
    return washers?.find(w => w.id === id)?.name || 'Desconhecido'
  }

  const days = Array.from({ length: 7 }, (_, i) => addDays(weekStart, i))

  return (
    <div className="p-6 space-y-6">
      <div className="flex justify-between items-center">
        <div>
          <h1 className="text-2xl font-bold text-slate-100 flex items-center gap-2">
            <CalendarIcon className="w-6 h-6 text-blue-500" />
            Agenda da Semana
          </h1>
          <p className="text-slate-400">
            {format(weekStart, "dd 'de' MMM", { locale: ptBR })} até {format(weekEnd, "dd 'de' MMM", { locale: ptBR })}
          </p>
        </div>
        
        <div className="flex gap-2">
          <button 
            onClick={() => setCurrentDate(addDays(currentDate, -7))}
            className="px-4 py-2 bg-slate-800 hover:bg-slate-700 text-slate-200 rounded-md border border-slate-700"
          >
            Anterior
          </button>
          <button 
            onClick={() => setCurrentDate(new Date())}
            className="px-4 py-2 bg-slate-800 hover:bg-slate-700 text-slate-200 rounded-md border border-slate-700"
          >
            Hoje
          </button>
          <button 
            onClick={() => setCurrentDate(addDays(currentDate, 7))}
            className="px-4 py-2 bg-slate-800 hover:bg-slate-700 text-slate-200 rounded-md border border-slate-700"
          >
            Próxima
          </button>
        </div>
      </div>

      <div className="grid grid-cols-7 gap-4">
        {days.map(day => {
          const dayAppointments = appointments?.filter(a => 
            a.scheduled_at && a.scheduled_at.startsWith(format(day, 'yyyy-MM-dd'))
          ) || []

          const isToday = format(day, 'yyyy-MM-dd') === format(new Date(), 'yyyy-MM-dd')

          return (
            <div key={day.toISOString()} className="bg-slate-800/50 rounded-xl border border-slate-700 p-4 min-h-[500px]">
              <div className="text-center mb-4 pb-4 border-b border-slate-700">
                <div className="text-sm text-slate-400 capitalize">{format(day, 'EEEE', { locale: ptBR })}</div>
                <div className={isToday ? "text-xl font-bold text-blue-500" : "text-xl font-bold text-slate-200"}>
                  {format(day, 'dd')}
                </div>
              </div>

              <div className="space-y-3">
                {isLoading ? (
                  <div className="text-slate-500 text-sm text-center">Carregando...</div>
                ) : dayAppointments.length === 0 ? (
                  <div className="text-slate-600 text-sm text-center">Livre</div>
                ) : (
                  dayAppointments.map(appt => (
                    <div key={appt.id} className="bg-slate-900 border border-slate-700 p-3 rounded-lg text-sm">
                      <div className="flex items-center gap-1.5 text-blue-400 font-semibold mb-2">
                        <Clock className="w-3.5 h-3.5" />
                        {format(parseISO(appt.scheduled_at), 'HH:mm')}
                      </div>
                      
                      <div className="space-y-1.5">
                        <div className="flex items-start gap-1.5 text-slate-200">
                          <Car className="w-3.5 h-3.5 mt-0.5 text-slate-400 shrink-0" />
                          <div className="leading-tight">
                            <span className="font-medium">{appt.vehicle_model}</span>
                            <div className="text-slate-400 text-xs mt-0.5">{appt.plate}</div>
                          </div>
                        </div>

                        <div className="flex items-center gap-1.5 text-slate-300">
                          <User className="w-3.5 h-3.5 text-slate-400 shrink-0" />
                          <div className="truncate">{appt.customer_name}</div>
                        </div>

                        <div className="text-xs text-slate-500 mt-2 pt-2 border-t border-slate-800">
                          Lavador: {getWasherName(appt.assigned_to)}
                        </div>
                      </div>
                    </div>
                  ))
                )}
              </div>
            </div>
          )
        })}
      </div>
    </div>
  )
}
